// Pure presentation logic for the home page — kept out of the components so
// it can be unit-tested without rendering (same pattern as pandaUsage).

export const greetingForHour = (hour: number): string => {
  if (hour >= 5 && hour < 12) return 'Good morning';
  if (hour >= 12 && hour < 18) return 'Good afternoon';
  return 'Good evening';
};

// The guest identity has no profile, so useUserProfile falls back to the raw
// entity ref ("user:development/guest") — greet with a clean name instead.
export const greetingNameFor = (
  displayName: string | undefined,
): string | undefined => {
  if (!displayName) return undefined;
  if (!displayName.includes(':')) return displayName;
  const name = displayName.split('/').pop() ?? displayName;
  return name.charAt(0).toUpperCase() + name.slice(1);
};

// Microsoft display names arrive as "LASTNAME First (ORG)"; the menu header
// shows the name without the org marker.
export const accountNameFor = (
  displayName: string | undefined,
): string | undefined => {
  const cleaned = greetingNameFor(displayName);
  return cleaned?.replace(/\s*\([^)]*\)\s*$/, '').trim() || undefined;
};

export const initialsFor = (name: string | undefined): string => {
  const words = name?.trim().split(/\s+/).filter(Boolean) ?? [];
  const first = words[0]?.charAt(0) ?? '';
  const last = words.length > 1 ? words[words.length - 1].charAt(0) : '';
  return (first + last).toUpperCase() || '?';
};

export const pluralize = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

export const formatTimeAgo = (timestamp: number, now: number): string => {
  const elapsed = Math.max(0, now - timestamp);
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
};

const KIND_BADGES: Record<string, string> = {
  component: 'COMP',
  api: 'API',
  template: 'TMPL',
  group: 'GROUP',
  user: 'USER',
  system: 'SYS',
  domain: 'DOMAIN',
  resource: 'RES',
};

export const visitBadge = (visit: {
  pathname: string;
  entityRef?: string;
}): string => {
  const kind = visit.entityRef?.split(':')[0]?.toLowerCase();
  if (kind && KIND_BADGES[kind]) return KIND_BADGES[kind];
  if (visit.pathname.startsWith('/docs')) return 'DOCS';
  if (visit.pathname.startsWith('/create')) return 'TMPL';
  return 'PAGE';
};

const AREA_LABELS: Record<string, string> = {
  catalog: 'Catalog',
  docs: 'Docs',
  create: 'Self Services',
};

export const visitContext = (pathname: string): string => {
  const [first] = pathname.replace(/^\//, '').split('/');
  return AREA_LABELS[first] ?? first;
};

export type KindCounts = {
  components: number;
  apis: number;
  templates: number;
};

export const summarizeKindFacets = (
  facets: Array<{ value: string; count: number }>,
): KindCounts => {
  const byKind = new Map(
    facets.map(f => [f.value.toLowerCase(), f.count] as const),
  );
  return {
    components: byKind.get('component') ?? 0,
    apis: byKind.get('api') ?? 0,
    templates: byKind.get('template') ?? 0,
  };
};

// The six TotalEnergies spectrum hues as diffuse glows along the top of the
// hero ("aurora"): [rgb triplet, x position, opacity on light surfaces].
const AURORA_GLOWS: Array<[string, string, number]> = [
  ['237, 0, 0', '10%', 0.11],
  ['255, 125, 0', '30%', 0.11],
  ['255, 199, 0', '50%', 0.1],
  ['77, 163, 47', '68%', 0.1],
  ['0, 163, 224', '85%', 0.12],
  ['51, 96, 246', '100%', 0.15],
];

export const auroraBackground = (mode: 'light' | 'dark'): string => {
  // Midnight surfaces are darker, so the glows need more presence.
  const boost = mode === 'dark' ? 1.5 : 1;
  return AURORA_GLOWS.map(([rgb, x, opacity], i) => {
    const last = i === AURORA_GLOWS.length - 1;
    const size = last ? '680px 260px' : '600px 230px';
    let y = i % 2 ? '-25%' : '-20%';
    if (last) y = '-15%';
    const fade = last ? '72%' : '68%';
    const alpha = Math.round(opacity * boost * 100) / 100;
    return `radial-gradient(${size} at ${x} ${y}, rgba(${rgb}, ${alpha}), transparent ${fade})`;
  }).join(', ');
};

export const squadFromOwnership = (
  ownershipEntityRefs: string[],
): string | undefined => {
  const group = ownershipEntityRefs.find(ref => ref.startsWith('group:'));
  return group?.split('/').pop();
};
